import React, { useState, useEffect, useRef } from 'react';
import './LiveStreamModal.css';
import { loadPlayer } from 'rtsp-relay/browser';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';

import VideocamIcon from '@mui/icons-material/Videocam';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import CloseIcon from '@mui/icons-material/Close';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import RemoveIcon from '@mui/icons-material/Remove';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';

import { cleanPrefixName, formatDeviceId, getDeviceStreamUrl, getDevicePublishAudioUrl } from '../../config/streams';

const LiveStreamModal = ({ isOpen, onClose, deviceId = '10', deviceName = '', streamPrefix = 'Cam' }) => {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const audioRef = useRef(null);
  const [activeTab, setActiveTab] = useState('video');
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [playerState, setPlayerState] = useState('normal'); // 'normal' | 'maximized' | 'minimized'

  const [isPublishingAudio, setIsPublishingAudio] = useState(false);
  const mediaRecorderRef = useRef(null);
  const pubWsRef = useRef(null);
  const micStreamRef = useRef(null);

  const activePrefix = cleanPrefixName(streamPrefix || localStorage.getItem('stream_prefix') || 'Cam');
  const cleanPrefix = activePrefix;
  const formattedDevId = formatDeviceId(deviceId);

  const videoUrl = getDeviceStreamUrl('video', deviceId, activePrefix);
  const thermalUrl = getDeviceStreamUrl('thermal', deviceId, activePrefix);
  const audioUrl = getDeviceStreamUrl('audio', deviceId, activePrefix);
  const publishAudioUrl = getDevicePublishAudioUrl(deviceId, activePrefix);
  const httpAudioStreamUrl = `${process.env.REACT_APP_BASEURL || 'http://localhost:8080'}/admin/stream/audio?url=${encodeURIComponent(audioUrl)}`;

  const streams = [
    { id: 'video', label: 'Video', icon: <VideocamIcon fontSize="small" />, url: videoUrl },
    { id: 'thermal', label: 'Thermal', icon: <VideocamIcon fontSize="small" style={{ color: '#ff9800' }} />, url: thermalUrl }
  ];

  const currentStream = streams.find(s => s.id === activeTab) || streams[0];

  const stopPublishingAudio = () => {
    setIsPublishingAudio(false);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch (e) {}
    }
    if (micStreamRef.current) {
      try {
        micStreamRef.current.getTracks().forEach(track => track.stop());
      } catch (e) {}
      micStreamRef.current = null;
    }
    if (pubWsRef.current) {
      try { pubWsRef.current.close(); } catch (e) {}
      pubWsRef.current = null;
    }
  };

  const startPublishingAudio = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;

      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.onended = () => {
          stopPublishingAudio();
        };
      }

      const rawBase = process.env.REACT_APP_BASEURL || 'http://localhost:8080';
      const wsBaseUrl = rawBase.replace(/^http/, 'ws');
      const wsUrl = `${wsBaseUrl}/admin/stream/publish-audio?url=${encodeURIComponent(publishAudioUrl)}`;
      const ws = new WebSocket(wsUrl);
      pubWsRef.current = ws;

      ws.onopen = () => {
        let mimeType = 'audio/webm;codecs=opus';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'audio/webm';
          if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = '';
          }
        }

        const options = mimeType ? { mimeType } : undefined;
        const mediaRecorder = new MediaRecorder(stream, options);
        mediaRecorderRef.current = mediaRecorder;

        mediaRecorder.ondataavailable = async (event) => {
          if (event.data && event.data.size > 0 && ws.readyState === WebSocket.OPEN) {
            try {
              const buffer = await event.data.arrayBuffer();
              ws.send(buffer);
            } catch (sendErr) {
              console.error('Error sending audio data buffer:', sendErr);
            }
          }
        };

        mediaRecorder.start(500);
        setIsPublishingAudio(true);
      };

      ws.onerror = (err) => {
        console.error('Mic publish WebSocket error:', err);
        stopPublishingAudio();
      };

      ws.onclose = () => {
        stopPublishingAudio();
      };
    } catch (err) {
      console.error('Error starting audio publishing:', err);
      stopPublishingAudio();
    }
  };

  const togglePublishAudio = () => {
    if (isPublishingAudio) {
      stopPublishingAudio();
    } else {
      startPublishingAudio();
    }
  };

  useEffect(() => {
    return () => {
      stopPublishingAudio();
    };
  }, []);

  const destroyPlayer = () => {
    if (playerRef.current) {
      try {
        if (typeof playerRef.current.destroy === 'function') {
          playerRef.current.destroy();
        } else if (typeof playerRef.current.stop === 'function') {
          playerRef.current.stop();
        }
      } catch (err) {
        console.warn('Error destroying player:', err);
      }
      playerRef.current = null;
    }
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.src = '';
      } catch (e) {}
    }
  };

  const initPlayer = (streamType, streamObj) => {
    destroyPlayer();
    if (!containerRef.current) return;

    containerRef.current.innerHTML = '';
    setLoading(true);

    const canvas = document.createElement('canvas');
    canvas.className = 'vlc_canvas';
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.objectFit = 'contain';
    canvas.style.display = 'block';
    containerRef.current.appendChild(canvas);

    const rawBase = process.env.REACT_APP_BASEURL || 'http://localhost:8080';
    const wsBaseUrl = rawBase.replace(/^http/, 'ws');
    const wsUrl = `${wsBaseUrl}/admin/stream/api/stream?url=${encodeURIComponent(streamObj.url)}`;

    loadPlayer({
      url: wsUrl,
      canvas: canvas,
      audio: false,
      pauseWhenHidden: false,
      videoBufferSize: 2 * 1024 * 1024,
      onDisconnect: () => {
        setLoading(false);
      }
    })
      .then(p => {
        playerRef.current = p;
        setLoading(false);
      })
      .catch(err => {
        console.error('Error loading VLC stream:', err);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        initPlayer(activeTab, currentStream);
      }, 50);

      return () => {
        clearTimeout(timer);
        destroyPlayer();
        stopPublishingAudio();
      };
    } else {
      destroyPlayer();
      stopPublishingAudio();
    }
  }, [isOpen, activeTab, currentStream.url]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = isMuted;
      if (!isMuted && isPlaying && audioRef.current.paused) {
        audioRef.current.play().catch(e => console.warn('Audio play error:', e));
      }
    }
  }, [isMuted, isPlaying]);

  useEffect(() => {
    if (isOpen && audioRef.current) {
      audioRef.current.src = httpAudioStreamUrl;
      audioRef.current.muted = isMuted;
      if (isPlaying) {
        audioRef.current.play().catch(err => {
          console.warn('Live audio play error:', err);
        });
      }
    }
  }, [isOpen, httpAudioStreamUrl]);

  const togglePlayPause = () => {
    if (isPlaying) {
      if (playerRef.current) {
        if (typeof playerRef.current.pause === 'function') {
          playerRef.current.pause();
        } else if (typeof playerRef.current.stop === 'function') {
          playerRef.current.stop();
        }
      }
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setIsPlaying(false);
    } else {
      if (playerRef.current) {
        if (typeof playerRef.current.play === 'function') {
          playerRef.current.play();
        }
      }
      if (audioRef.current) {
        audioRef.current.src = httpAudioStreamUrl;
        audioRef.current.muted = isMuted;
        audioRef.current.play().catch(e => console.warn(e));
      }
      setIsPlaying(true);
    }
  };

  const toggleMute = () => {
    setIsMuted(prev => !prev);
  };

  const toggleMaximize = () => {
    setPlayerState(prev => (prev === 'maximized' ? 'normal' : 'maximized'));
  };

  const toggleMinimize = () => {
    setPlayerState(prev => (prev === 'minimized' ? 'normal' : 'minimized'));
  };

  if (!isOpen) return null;

  if (playerState === 'minimized') {
    return (
      <div className="vlc_mini_bar" onClick={() => setPlayerState('normal')}>
        <div className="vlc_mini_info">
          <span className="vlc_live_dot"></span>
          <span className="vlc_mini_title">VLC - {cleanPrefix}-{formattedDevId}</span>
        </div>
        <div className="vlc_mini_actions">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleMaximize(); }} style={{ color: '#ff9800' }}>
            <OpenInFullIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onClose(); }} style={{ color: '#fff' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </div>
    );
  }

  return (
    <div className={`vlc_overlay ${playerState === 'maximized' ? 'maximized_overlay' : ''}`} onClick={onClose}>
      <div
        className={`vlc_player_window ${playerState === 'maximized' ? 'maximized_window' : ''}`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="vlc_header">
          <div className="vlc_title">
            <div className="vlc_cone_icon">▲</div>
            <span>VLC Player - {deviceName || `${cleanPrefix}-${formattedDevId}`}</span>
          </div>

          <div className="vlc_window_controls">
            <Chip label={`${cleanPrefix}-${formattedDevId}`} className="vlc_chip" size="small" />
            <IconButton onClick={toggleMinimize} size="small" title="Minimize" style={{ color: '#fff' }}>
              <RemoveIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={toggleMaximize} size="small" title={playerState === 'maximized' ? 'Restore' : 'Maximize'} style={{ color: '#ff9800' }}>
              {playerState === 'maximized' ? <CloseFullscreenIcon fontSize="small" /> : <OpenInFullIcon fontSize="small" />}
            </IconButton>
            <IconButton onClick={onClose} size="small" title="Close" style={{ color: '#ef5350' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        {/* Stream Selector Tabs */}
        <div className="vlc_tabs_bar">
          {streams.map(s => (
            <button
              key={s.id}
              className={`vlc_tab_btn ${activeTab === s.id ? 'active' : ''}`}
              onClick={() => setActiveTab(s.id)}
            >
              {s.icon}
              <span>{s.label}</span>
            </button>
          ))}
        </div>

        {/* Video / Visual Stage */}
        <div className="vlc_screen_stage">
          <div className="vlc_live_badge">
            <span className="vlc_red_pulse">●</span> LIVE
          </div>

          {loading && (
            <div style={{ position: 'absolute', display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 10 }}>
              <CircularProgress style={{ color: '#ff9800' }} size={40} />
              <div style={{ marginTop: '10px', fontSize: '0.8rem', color: '#ff9800', fontWeight: 'bold' }}>
                Connecting RTSP Stream...
              </div>
            </div>
          )}

          <div className="vlc_canvas_wrapper" ref={containerRef} />

          {/* Synchronized Live Audio Stream */}
          <audio
            ref={audioRef}
            src={httpAudioStreamUrl}
            autoPlay
            playsInline
            muted={isMuted}
            style={{ display: 'none' }}
          />
        </div>

        {/* Controls Bar */}
        <div className="vlc_controls_bar">
          <div className="vlc_ctrl_left">
            <IconButton onClick={togglePlayPause} style={{ color: '#fff' }} title={isPlaying ? 'Pause' : 'Play'}>
              {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
            </IconButton>
            <IconButton onClick={toggleMute} style={{ color: isMuted ? '#ef5350' : '#fff' }} title={isMuted ? 'Unmute' : 'Mute'}>
              {isMuted ? <VolumeOffIcon /> : <VolumeUpIcon />}
            </IconButton>
          </div>

          <div className="vlc_ctrl_center">
            <div className="vlc_status_text">
              <span>{currentStream.label}</span>
              <span className="vlc_url_display">{currentStream.url}</span>
            </div>
          </div>

          <div className="vlc_ctrl_right">
            <IconButton
              onClick={togglePublishAudio}
              style={{
                color: isPublishingAudio ? '#ff1744' : '#ffffff',
                backgroundColor: isPublishingAudio ? 'rgba(255, 23, 68, 0.2)' : 'transparent',
                borderRadius: '50%'
              }}
              title={isPublishingAudio ? 'Stop Talking' : 'Hold to Speak (Talkback)'}
            >
              {isPublishingAudio ? <MicIcon className="vlc_red_pulse" /> : <MicOffIcon />}
            </IconButton>
            <IconButton onClick={toggleMaximize} style={{ color: '#fff' }} title="Full Window">
              <OpenInFullIcon fontSize="small" />
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveStreamModal;
